/**
 * Smart model selection router
 * V1.1: Device-aware model selection
 */
import { SDKError } from '../errors.js';
/**
 * Score a model for ranking.
 * Higher score = better fit.
 */
function scoreModel(model, device, preference) {
    let score = 100; // Base score
    const reasons = [];
    const warnings = [];
    const maxSizeMB = device.inferenceHints.recommendedMaxModelSizeMB;
    const modelSizeMB = model.sizeMB ?? 0;
    // === Size constraints ===
    if (modelSizeMB > 0) {
        if (modelSizeMB > maxSizeMB * 1.2) {
            // Model exceeds limit by >20% - strong penalty
            score -= 50;
            warnings.push(`Model size (${modelSizeMB}MB) exceeds recommended limit (${maxSizeMB}MB) by >20%`);
        }
        else if (modelSizeMB > maxSizeMB) {
            // Model slightly exceeds limit - mild penalty
            score -= 20;
            warnings.push(`Model size (${modelSizeMB}MB) slightly exceeds recommended limit (${maxSizeMB}MB)`);
        }
        else if (modelSizeMB <= maxSizeMB * 0.5) {
            // Model is well under limit - good
            score += 10;
            reasons.push(`Model size (${modelSizeMB}MB) is well within device limits`);
        }
    }
    // === Tier matching ===
    const preferredTier = preference.tier ?? 'balanced';
    const modelTier = model.tier ?? inferTierFromSize(modelSizeMB);
    if (modelTier === preferredTier) {
        score += 20;
        reasons.push(`Matches requested tier: ${preferredTier}`);
    }
    else if (preferredTier === 'balanced') {
        // Balanced accepts any tier with slight preference for middle sizes
        if (modelTier === 'fast')
            score += 5;
        if (modelTier === 'quality')
            score += 5;
    }
    else if (preferredTier === 'fast' && modelTier !== 'fast') {
        score -= 15;
    }
    else if (preferredTier === 'quality' && modelTier !== 'quality') {
        score -= 15;
    }
    // === Tag matching ===
    if (preference.tag && model.tags) {
        if (model.tags.includes(preference.tag)) {
            score += 15;
            reasons.push(`Matches requested tag: ${preference.tag}`);
        }
    }
    // === Memory requirements ===
    const deviceMemoryGB = device.memory?.deviceMemoryGB;
    if (model.minRecommendedMemoryGB && deviceMemoryGB) {
        if (deviceMemoryGB < model.minRecommendedMemoryGB) {
            score -= 30;
            warnings.push(`Device memory (${deviceMemoryGB}GB) below model requirement (${model.minRecommendedMemoryGB}GB)`);
        }
    }
    // === Prefer smaller models when tied ===
    // Slight preference for smaller models (faster download, less risk)
    if (modelSizeMB > 0) {
        score -= modelSizeMB / 100; // -1 point per 100MB
    }
    return { score, reasons, warnings };
}
/**
 * Infer tier from model size if not specified.
 */
function inferTierFromSize(sizeMB) {
    if (sizeMB <= 500)
        return 'fast';
    if (sizeMB <= 1500)
        return 'balanced';
    return 'quality';
}
/**
 * Select the best model for the given device and preferences.
 *
 * @throws SDKError with code MODEL_NOT_FOUND if no suitable model found
 */
export function selectModel(input) {
    const { device, models, requestedModel, engine, preference = {} } = input;
    // If a specific model is requested (not 'auto'), validate and return it
    if (requestedModel && requestedModel !== 'auto') {
        const model = models.find((m) => m.id === requestedModel);
        if (!model) {
            throw new SDKError('MODEL_NOT_FOUND', `Model "${requestedModel}" not found in manifest. ` +
                `Available models: ${models.map((m) => m.id).join(', ')}`);
        }
        // Still score it to provide warnings
        const { reasons, warnings } = scoreModel(model, device, preference);
        return {
            selected: model,
            candidates: [model],
            reasons: [`Explicitly requested model: ${requestedModel}`, ...reasons],
            warnings: warnings.length > 0 ? warnings : undefined,
        };
    }
    // Filter models by engine
    const engineModels = models.filter((m) => m.engine === engine);
    if (engineModels.length === 0) {
        throw new SDKError('MODEL_NOT_FOUND', `No models available for engine "${engine}"`);
    }
    // Score all models
    const scored = engineModels.map((model) => {
        const { score, reasons, warnings } = scoreModel(model, device, preference);
        return { model, score, reasons, warnings };
    });
    // Sort by score (descending)
    scored.sort((a, b) => b.score - a.score);
    const best = scored[0];
    const candidates = scored.map((s) => s.model);
    // Aggregate all unique reasons
    const allReasons = ['Auto-selected based on device capabilities', ...best.reasons];
    // Collect warnings from top candidate
    const allWarnings = best.warnings.length > 0 ? best.warnings : undefined;
    return {
        selected: best.model,
        candidates,
        reasons: allReasons,
        warnings: allWarnings,
    };
}
/**
 * Get alternative models smaller than the given model.
 * Used for suggesting fallbacks on load failure.
 */
export function getSmallerAlternatives(failedModelId, models, maxCount = 3) {
    const failedModel = models.find((m) => m.id === failedModelId);
    if (!failedModel)
        return [];
    const failedSizeMB = failedModel.sizeMB ?? Infinity;
    // Find smaller models
    const smaller = models
        .filter((m) => m.id !== failedModelId && (m.sizeMB ?? 0) < failedSizeMB)
        .sort((a, b) => (b.sizeMB ?? 0) - (a.sizeMB ?? 0)); // Largest of the smaller first
    return smaller.slice(0, maxCount);
}
//# sourceMappingURL=selectModel.js.map